// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#417) — pure reader for the `resolveInvoiceFromEventLog`
// response: turn the Lambda's recovery payload into the same `{ doc,
// documentId }` shape the normal ActiveQueue resolve produces, so everything
// downstream (the facts header, lane ① EventLog, lanes ②③ FileMaker, the
// Claude conclusion, the Time Machine history section) runs unchanged.
//
// Extracted rather than inlined because Ticket Master gates EVERY lane on
// `validDocId` — if this returns a malformed id the tool silently goes blank
// again, which is the exact failure #417 is fixing. A pure function lets the
// tests import the real code path instead of an approximation.
//
// Returns null when nothing usable came back, so the caller can fall through to
// the existing "No document found" message rather than rendering a half-empty
// header.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const OBJECTID_HEX_RE = /^[0-9a-fA-F]{24}$/;

/**
 * @param {object} result — the Lambda's `message.result` payload
 * @returns {{ doc: object, documentId: string, recoveredFrom: { operation: string|null, timestamp: string|null } } | null}
 */
export function pickRecoveredJobDoc(result) {
  if (!result || typeof result !== "object") return null;
  // The Lambda reports its own failures in-band rather than throwing.
  if (result.error) return null;

  const documentId = typeof result.documentId === "string" ? result.documentId : null;
  if (!documentId || !OBJECTID_HEX_RE.test(documentId)) return null;

  const doc = result.doc;
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return null;
  if (Object.keys(doc).length === 0) return null;

  const from = result.recoveredFrom && typeof result.recoveredFrom === "object"
    ? result.recoveredFrom
    : {};

  return {
    // Keep the recovered image's own `_id` consistent with the id every lane is
    // keyed by — they come from the same event, but a mismatch here would point
    // the header at one job and the history at another.
    doc: { ...doc, _id: documentId },
    documentId,
    recoveredFrom: {
      operation: typeof from.operation === "string" ? from.operation : null,
      timestamp: typeof from.timestamp === "string" ? from.timestamp : null,
    },
  };
}

/**
 * Format the recovery timestamp for the notice line. Returns "" for anything
 * unparseable so the caller can render the notice without a date rather than
 * showing "Invalid Date" to a help-desk user.
 */
export function formatRecoveredDate(timestamp) {
  if (typeof timestamp !== "string" || timestamp === "") return "";
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export const __testables__ = { OBJECTID_HEX_RE };
